import { useEffect, useMemo, useState } from 'react';
import {
  ACGME_KINDS,
  acgmeLabel,
  api,
  levelLabel,
  personName,
  type AcademicBlock,
  type AcgmeTotals,
  type BlockAssignmentRow,
  type ProgramRole,
  type ResidencyRotation,
  type ResidentYearRow,
} from '../lib/api';
import { navigate } from '../lib/router';
import { Gate, ProgramHeader, useProgram, type Program } from './Residency';

/**
 * The program's home (D50).
 *
 * Until now every login landed on the hospitalist dashboard — a week of
 * hospitals, which for a residency is an empty box with the wrong words in
 * it. A residency does not have a week; it has a block, and the question a
 * director or a chief opens the page to answer is "where are we, and is
 * anything wrong".
 *
 * So: where the year has got to, who is on what right now, and a list of
 * things that need somebody. Every item on that list is computed from the
 * program's own data — nothing here asserts an ACGME threshold this platform
 * has not been told, because a made-up target that looks official is worse
 * than no target at all.
 *
 * The most important state is the emptiest one. A program that has just been
 * set up has no year in it, and the only useful thing this page can say is
 * where to put one.
 */
export function ResidencyHome({ clients, scheduleId, year }: { clients: string[] | null; scheduleId: string | null; year: string | null }) {
  const loaded = useProgram(clients, scheduleId, year);
  if (loaded.state !== 'ready') return <Gate loaded={loaded} />;
  return (
    <section className="residency reshome">
      <ProgramHeader program={loaded.program} path="/admin" />
      <Body program={loaded.program} />
    </section>
  );
}

interface Loadout {
  roster: ResidentYearRow[];
  totals: AcgmeTotals[];
  rotations: ResidencyRotation[];
  roles: ProgramRole[];
  here: BlockAssignmentRow[];
}

function Body({ program }: { program: Program }) {
  const { clientId, schedule, year, blocks } = program;
  const [data, setData] = useState<Loadout | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Today against the program's own calendar. A block, not a week: this is
  // the unit the whole year is cut into, and "which week is it" is a
  // question nobody in a residency asks.
  const today = new Date().toISOString().slice(0, 10);
  const current = useMemo(() => {
    if (!blocks.length) return null;
    const inside = blocks.find((b) => b.startDate <= today && today <= b.endDate);
    if (inside) return { block: inside, when: 'now' as const };
    if (today < blocks[0].startDate) return { block: blocks[0], when: 'ahead' as const };
    return { block: blocks[blocks.length - 1], when: 'past' as const };
  }, [blocks, today]);

  useEffect(() => {
    let live = true;
    setData(null);
    setError(null);
    if (!year || !current) return;
    Promise.all([
      api.residencyRoster(clientId, schedule.id, year),
      api.residencyAcgme(clientId, schedule.id, year),
      api.residencyRotations(clientId, schedule.id),
      api.programRoles(clientId, schedule.id).catch(() => [] as ProgramRole[]),
      api.residencyAssignments(clientId, schedule.id, { year, block: current.block.number }),
    ])
      .then(([roster, totals, rotations, roles, here]) => live && setData({ roster, totals, rotations, roles, here }))
      .catch((e: Error) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [clientId, schedule.id, year, current?.block.number]);

  if (!year || !blocks.length) return <NothingYet />;
  if (error) return <p className="error">Couldn't load the program ({error}).</p>;
  if (!data || !current) return <p className="muted">Loading…</p>;

  return (
    <>
      <WhereWeAre current={current} blocks={blocks} schedule={schedule.id} year={year} />
      <div className="dash-cols">
        <NeedsSomebody data={data} blocks={blocks} current={current} schedule={schedule.id} year={year} />
        <ThisBlock rows={data.here} block={current.block} schedule={schedule.id} year={year} />
      </div>
      <TheCohort data={data} blocks={blocks} />
    </>
  );
}

/** The state a new program is actually in, and the only thing worth saying in it. */
function NothingYet() {
  return (
    <div className="card">
      <h2>No year loaded yet</h2>
      <p>
        This program is set up, and nothing has been uploaded into it. A year arrives whole — every resident, every block —
        and is checked before a single row is written.
      </p>
      <p>
        <button className="primary" type="button" onClick={() => navigate('/admin/residency/import')}>Upload a year</button>
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Where the year has got to
// ---------------------------------------------------------------------------

function WhereWeAre({
  current,
  blocks,
  schedule,
  year,
}: {
  current: { block: AcademicBlock; when: 'now' | 'ahead' | 'past' };
  blocks: AcademicBlock[];
  schedule: string;
  year: string;
}) {
  const { block, when } = current;
  const today = new Date().toISOString().slice(0, 10);
  const dayOf = days(block.startDate, today) + 1;
  const length = days(block.startDate, block.endDate) + 1;
  const index = blocks.findIndex((b) => b.id === block.id) + 1;

  return (
    <div className="card wherewe">
      <h2>
        {when === 'now' ? `Block ${block.number}` : when === 'ahead' ? `The year starts with block ${block.number}` : `The year ended with block ${block.number}`}
        <span className="muted">
          {block.startDate} → {block.endDate}
        </span>
      </h2>
      {when === 'now' ? (
        <p>
          Day <strong>{dayOf}</strong> of {length}. Block <strong>{index}</strong> of {blocks.length} in {year}.
        </p>
      ) : when === 'ahead' ? (
        <p className="muted">{year} has not started yet — it opens on {block.startDate}.</p>
      ) : (
        <p className="muted">{year} finished on {block.endDate}. Nothing below is live; it is the record of that year.</p>
      )}
      <ul className="blockstrip">
        {blocks.map((b) => (
          <li key={b.id}>
            <button
              type="button"
              className={b.id === block.id ? 'current' : b.endDate < today ? 'done' : undefined}
              onClick={() => navigate(`/admin/residency/blocks?scheduleId=${schedule}&year=${year}&block=${b.number}`)}
              title={`${b.startDate} → ${b.endDate}`}
            >
              <strong>{b.number}</strong>
              <span>{b.startDate.slice(8, 10)}/{b.startDate.slice(5, 7)}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// What needs somebody
// ---------------------------------------------------------------------------

/**
 * Only things the program's own data actually says. No invented ACGME
 * thresholds: this platform has not been told what a graduating total must
 * be, and a number that looks official and is guessed would be believed.
 */
function NeedsSomebody({
  data,
  blocks,
  current,
  schedule,
  year,
}: {
  data: Loadout;
  blocks: AcademicBlock[];
  current: { when: 'now' | 'ahead' | 'past' };
  schedule: string;
  year: string;
}) {
  const items: { key: string; tone: 'err' | 'warn' | 'info'; body: React.ReactNode }[] = [];
  const to = (path: string) => () => navigate(path);

  const short = data.totals.filter((t) => t.blocks < blocks.length);
  if (short.length) {
    items.push({
      key: 'short',
      tone: 'err',
      body: (
        <>
          <strong>{short.length} resident{short.length === 1 ? ' has' : 's have'} a hole in the year</strong> — fewer than{' '}
          {blocks.length} blocks on the board. A resident with a gap nobody sees is the thing the import exists to prevent, so
          this means the uploaded file had one.{' '}
          <button className="linklike" type="button" onClick={to(`/admin/residency?scheduleId=${schedule}&year=${year}`)}>see the roster</button>
        </>
      ),
    });
  }

  const unclassified = data.rotations.filter((r) => r.acgmeKind === 'other' && r._count.assignments > 0);
  if (unclassified.length) {
    const resident = unclassified.reduce((n, r) => n + r._count.assignments, 0);
    items.push({
      key: 'kinds',
      tone: 'warn',
      body: (
        <>
          <strong>{unclassified.length} rotation{unclassified.length === 1 ? '' : 's'} nobody has classified</strong> —{' '}
          {unclassified.map((r) => r.name).slice(0, 4).join(', ')}
          {unclassified.length > 4 ? ' and others' : ''}, covering {resident.toLocaleString()} resident-block
          {resident === 1 ? '' : 's'}. Until the program says what they count as, those weeks count towards nothing.
        </>
      ),
    });
  }

  const directors = data.roles.filter((r) => r.role === 'director' && r.active);
  if (!directors.length) {
    items.push({
      key: 'director',
      tone: 'err',
      body: (
        <>
          <strong>Nobody holds the program director role</strong>, so nobody can hand out any other role. Until someone does,
          chief residents and coordinators cannot be added.{' '}
          <button className="linklike" type="button" onClick={to(`/admin/residency/roles?scheduleId=${schedule}&year=${year}`)}>who runs it</button>
        </>
      ),
    });
  } else if (data.roles.filter((r) => r.active).length === directors.length) {
    items.push({
      key: 'alone',
      tone: 'info',
      body: (
        <>
          The director is the only person with a role on this program. Chief residents and coordinators can be added without
          anyone calling CallCovered.{' '}
          <button className="linklike" type="button" onClick={to(`/admin/residency/roles?scheduleId=${schedule}&year=${year}`)}>who runs it</button>
        </>
      ),
    });
  }

  if (current.when === 'past') {
    items.push({
      key: 'ended',
      tone: 'info',
      body: <><strong>{year} has finished.</strong> The next year is uploaded the same way this one was.</>,
    });
  }

  // "Needs somebody" means somebody has to do something. An item that is
  // merely worth knowing does not belong under that heading — a heading that
  // cries wolf on a healthy program is one nobody reads on a sick one.
  const problems = items.filter((i) => i.tone !== 'info');
  const notes = items.filter((i) => i.tone === 'info');

  return (
    <section className="card">
      <h2>Needs somebody</h2>
      {problems.length === 0 ? (
        <p className="muted">
          Nothing outstanding. Every resident has a full year on the board, every rotation counts as something, and the
          program has its own people running it.
        </p>
      ) : (
        <ul className="attention">
          {problems.map((i) => (
            <li key={i.key}>
              <span className={`dot ${i.tone}`} />
              <span>{i.body}</span>
            </li>
          ))}
        </ul>
      )}
      {notes.length > 0 && (
        <ul className="attention notes">
          {notes.map((i) => (
            <li key={i.key}>
              <span className="dot info" />
              <span>{i.body}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Who is on what, right now
// ---------------------------------------------------------------------------

function ThisBlock({ rows, block, schedule, year }: { rows: BlockAssignmentRow[]; block: AcademicBlock; schedule: string; year: string }) {
  const byRotation = useMemo(() => {
    const map = new Map<string, { kind: string; people: string[] }>();
    for (const r of rows) {
      const entry = map.get(r.rotation.name) ?? { kind: r.rotation.acgmeKind, people: [] };
      entry.people.push(personName(r.physician));
      map.set(r.rotation.name, entry);
    }
    return [...map.entries()].sort((a, b) => b[1].people.length - a[1].people.length || a[0].localeCompare(b[0]));
  }, [rows]);

  return (
    <section className="card">
      <h2>
        Where everybody is
        <button className="linklike" type="button" onClick={() => navigate(`/admin/residency/blocks?scheduleId=${schedule}&year=${year}&block=${block.number}`)}>
          the whole board
        </button>
      </h2>
      {!rows.length ? (
        <p className="muted">Nobody is scheduled in block {block.number}.</p>
      ) : (
        <ul className="orgchildren">
          {byRotation.map(([name, { kind, people }]) => (
            <li key={name}>
              <span className="orgname">{name}</span>
              <span className="chip">{acgmeLabel(kind)}</span>
              <span className="muted small" style={{ marginLeft: 'auto' }}>{people.length}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// The cohort
// ---------------------------------------------------------------------------

function TheCohort({ data, blocks }: { data: Loadout; blocks: AcademicBlock[] }) {
  const cohorts = new Map<string, number>();
  for (const r of data.roster) {
    const label = levelLabel(r);
    cohorts.set(label, (cohorts.get(label) ?? 0) + 1);
  }
  const blockTotal = data.totals.reduce((n, t) => n + t.blocks, 0);
  const kinds = ACGME_KINDS.filter((k) => data.totals.some((t) => t.kinds[k]));

  return (
    <div className="card">
      <h2>The program</h2>
      <ul className="orgstats">
        <li><strong>{data.roster.length}</strong> residents</li>
        {[...cohorts].sort((a, b) => a[0].localeCompare(b[0])).map(([label, n]) => (
          <li key={label}><strong>{n}</strong> {label}</li>
        ))}
        <li><strong>{blocks.length}</strong> blocks</li>
        <li><strong>{blockTotal.toLocaleString()}</strong> resident-blocks</li>
      </ul>
      {kinds.length > 0 && (
        <>
          <h3>The year, by what it counts as</h3>
          <ul className="orgstats">
            {kinds.map((k) => {
              const n = data.totals.reduce((sum, t) => sum + (t.kinds[k] ?? 0), 0);
              return (
                <li key={k}>
                  <strong>{n}</strong> {acgmeLabel(k)}{' '}
                  <span className="muted">{blockTotal ? `${((n / blockTotal) * 100).toFixed(1)}%` : ''}</span>
                </li>
              );
            })}
          </ul>
          <p className="small muted">
            One block is two weeks, so half an ACGME month. Counted once per rotation rather than once per spelling.
          </p>
        </>
      )}
    </div>
  );
}

const days = (from: string, to: string) =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
